// 動態 chunk 載入失敗的復原。依「本地快取是否真的壞掉」分成兩類處置，不可混用：
//
// A 類（內容不對）：`Invalid or unexpected token`（CDN 回傳損壞但 200 的 JS 被 Workbox 快取後永久
//   重現，CLIENT-PWA-SSR-BD）、`Unexpected end of input`（同樣是拿到損壞但 200 的 JS，差別只在損壞
//   位置落在檔尾，解析到 EOF 時括號／區塊仍未閉合；與 `Invalid or unexpected token` 同為 V8 對「本地
//   拿到的位元組壞掉」的兩種措辭，CLIENT-PWA-SSR-8Q 的 vue-error 事件即為此類）、
//   `does not provide an export named`（PWA 快取殘留舊 chunk，named export 與其他 chunk 對不上；
//   單一一致 build 內不可能發生，必為新舊 build 混用，
//   CLIENT-PWA-SSR-ZM）。壞的就是本地快取，必須清掉所有快取 + 反註冊 SW 才能復原。
//   註 1：只有「位元組已送達但解析失敗」才屬 A 類；若位元組根本沒送達，瀏覽器丟的是 B 類的 fetch 失敗措辭。
//   註 2：`JSON.parse('')` 的 `Unexpected end of JSON input` 不含本字串，不會被誤判為 chunk 損壞。
//   註 3：A 類的措辭必須逐一引擎補齊（B 類已列四種措辭）。`Unexpected end of script` 是 JavaScriptCore
//     對「解析到 EOF 時仍未閉合」的措辭，即 V8 `Unexpected end of input` 的 WebKit 對應版本
//     （CLIENT-PWA-SSR-1TB：iOS 第一方 async component chunk 被截斷，vue:error 有進到本檔但兩條 regex
//     皆未命中，iOS 因此完全拿不到復原，而同一情境的 Android 走 `Unexpected end of input` 會復原）。
//   註 4：JSC 另一個 EOF 措辭 `Unexpected EOF`（CLIENT-PWA-SSR-19D）仍不納入：JSC 的 JSON.parse 失敗訊息
//     為 `JSON Parse error: Unexpected EOF`，納入會讓一般 JSON 解析失敗被誤判成 chunk 損壞而清掉整包快取
//     （V8 側不存在此問題，故註 2 成立）。19D 屬另一個 issue，另案評估。
//
// B 類（取檔失敗）：四種引擎措辭的 module fetch 失敗（CLIENT-PWA-SSR-1A9 / H3）。2026-08-09 以 Sentry
//   資料驗證：事件的 gitSha 一律等於當時線上的 sha（pool/bundle.txt），且事件中的失敗 chunk URL
//   逐一實抓皆仍回 200 —— 檔案好好地在 CDN 上，本地快取沒有壞，屬行動網路／CDN 的偶發取檔失敗。
//   此時清快取只會讓使用者在同一條劣化連線上重抓整包 precache，是淨傷害，故僅重整不清快取。
//   仍需重整的原因：依 HTML 規範，取檔失敗的 module URL 會在該文件的 module map 內被標記為失敗，
//   同一份文件內後續 import() 只會立即回傳同一個 rejection，重試無效，只有換一份文件才能復原。
//
// 兩類各自持有獨立的 sessionStorage guard（每個 session 至多復原一次，避免無限重載循環）。
// guard 必須分開：B 類事件量約為 A 類的 66 倍（30 天 3,725 vs 56），共用一個 key 會讓 B 類幾乎
// 必然先把額度用掉，導致真正需要清快取的 A 類永遠等不到復原。
//
// A 類的「清掉所有快取」必須包含瀏覽器 HTTP cache：正式環境的 chunk 走跨網域的 app.cdnURL，而 precache 清單是
// 同源路徑、JS 的 runtime cache 規則（RegExpRoute）對跨網域 URL 又只接受從開頭比對成功者，chunk 從不進
// Cache Storage，壞檔只會留在 HTTP cache（CDN 回 max-age=31536000）。reload 不會重新驗證子資源，只清 Cache
// Storage 的話新頁面會原樣取回同一份壞檔（CLIENT-PWA-SSR-8Q 已有清快取重整後立刻再次失敗、guard 用盡而卡住
// 的實例）。錯誤本身不帶失敗的 chunk URL，故以 cache: 'reload' 重抓本頁載入過的全部第一方 JS，讓網路上的
// 完整版本覆蓋 HTTP cache。
// A 類錯誤另會標上本頁的復原狀態（app/utils/chunkRecovery.js），sentry.client.config.ts 依此分流上報：
// 觸發重整的那一筆代表復原進行中（降級保留為 CDN 健康度訊號），guard 已用盡的那一筆才代表使用者真的卡住。
import { chunkErrorEvent } from '#build/nuxt.config.mjs'
import { usePwaUpdate } from '@/composables/core/usePwaUpdate'
import { CHUNK_RECOVERY_STATUS, EXPORT_MISMATCH_RE, markChunkRecoveryStatus } from '@/utils/chunkRecovery'

const PURGE_GUARD_KEY = 'chunk-recovery-reloaded'
const RELOAD_GUARD_KEY = 'chunk-recovery-soft-reloaded'
// 重抓 chunk 的時間上限：連線劣化時寧可帶著未重抓完的快取重整，也不讓使用者無限期停在載入失敗的畫面
const HTTP_CACHE_REFRESH_TIMEOUT_MS = 10_000
const SCRIPT_URL_RE = /\.m?js(\?|$)/i
const MAX_OBSERVED_SCRIPT_URLS = 500

const CORRUPT_CHUNK_RE = new RegExp(
  ['Invalid or unexpected token', 'Unexpected end of input', 'Unexpected end of script', EXPORT_MISMATCH_RE.source].join('|'),
  'i'
)
// app.cdnURL 指向另一個 origin，lazy chunk 一律是跨網域的 module script、恆以 CORS 模式取檔，故 CDN 少回
// Access-Control-Allow-Origin（錯誤回應、中間層快取到無標頭的版本）時，WebKit 走的是 CORS 拒絕而非一般
// 取檔失敗，措辭與其他三種都不同，少了這條 iOS 不會觸發復原重整、只有 Android 會（CLIENT-PWA-SSR-H3）。
// 兩種變體都必須保留 `script load` / `.js` 這類腳本語意，不可只取共通後綴 `denied by Cross-Origin Resource
// Sharing policy` —— 該句在 WebKit 是 DocumentThreadableLoader（fetch / XHR 共用）的措辭，跨域 API 轉址失敗
// 同樣會命中；而本檔任何命中都會觸發整頁重整，誤觸會讓使用者填到一半的表單全部遺失（第三方注入腳本的
// 未捕捉 fetch rejection 確實會冒泡到本檔的 unhandledrejection listener，見 howler.client.js 檔頭的前例）。
const CORS_DENIED_SCRIPT_RE =
  /Cross-origin script load denied by Cross-Origin Resource Sharing policy|Cross-origin redirection to \S+\.m?js\S* denied by Cross-Origin Resource Sharing policy/i
// 修改此清單時請同步 sentry.client.config.ts 的 CHUNK_IMPORT_ERROR_RE / CORS_DENIED_SCRIPT_RE
const FETCH_CHUNK_RE = new RegExp(
  [
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    CORS_DENIED_SCRIPT_RE.source,
  ].join('|'),
  'i'
)

// Nuxt 4.2 內建的 chunkErrorEvent listener 直接讀 `event.payload.message`，而 Firefox 的 import() 可能以
// 非 Error 值 reject，Vite 原樣放進 payload，listener 便拋出未捕捉的 TypeError（CLIENT-PWA-SSR-15C，上游
// nuxt#35155 已改 optional chaining）。payload 不可替換成新 Error：nuxt:chunk-reload 以 identity 比對
// app:chunkError 收到的 error 與 router.onError 收到的 rejection（Vite 拋的是原值），替換會讓換頁重載失效。
// 故對這類事件代為觸發 hook 後截斷，其餘事件原樣放行。listener 必須在模組頂層註冊：同一 target 上的
// listener 依註冊順序執行（capture 也不例外，已以 Chromium 實測），而 Nuxt 在 createNuxtApp() 註冊自己的
// listener，只有 #build/plugins 被靜態 import 時求值的模組頂層早於它，plugin setup 已經太晚。
let chunkErrorNuxtApp = null
const handleMalformedChunkErrorEvent = (event) => {
  if (!chunkErrorNuxtApp || typeof event.payload?.message === 'string') return
  event.stopImmediatePropagation()
  chunkErrorNuxtApp.callHook('app:chunkError', { error: event.payload })
}
if (import.meta.client && chunkErrorEvent) {
  window.addEventListener(chunkErrorEvent, handleMalformedChunkErrorEvent)
}

export default defineNuxtPlugin((nuxtApp) => {
  chunkErrorNuxtApp = nuxtApp

  const runtimeConfig = useRuntimeConfig()
  // 本頁是否已觸發清快取重整。同一個錯誤會依序流經 app:chunkError、vue:error 等多個 hook，
  // 第二次進來時 guard 已是本頁自己寫入的，不可因此誤判為「復原已用盡」
  let isPurgeReloadPending = false

  // Resource Timing 緩衝區預設僅 250 筆，長 session 內圖片、測線與 beacon 會先佔滿，之後才載入的 chunk 不會出現在
  // getEntriesByType；observer 不受此上限影響。註冊前的 entry 由緩衝區補上（本外掛很早執行，那時緩衝區還不會滿）
  const observedScriptUrls = new Set()
  const scriptResourceObserver =
    typeof PerformanceObserver === 'function'
      ? new PerformanceObserver((list) => {
          for (const { name } of list.getEntries()) {
            if (observedScriptUrls.size < MAX_OBSERVED_SCRIPT_URLS && SCRIPT_URL_RE.test(name)) observedScriptUrls.add(name)
          }
        })
      : null
  scriptResourceObserver?.observe({ entryTypes: ['resource'] })

  // 第一方 chunk 一律位於 app.cdnURL（未設定時為同源 baseURL）下的 buildAssetsDir
  const resolveBuildAssetsUrl = () => {
    const { cdnURL, baseURL, buildAssetsDir } = runtimeConfig.app
    const assetsUrl = new URL(cdnURL || baseURL, window.location.origin)
    assetsUrl.pathname = `${assetsUrl.pathname.replace(/\/+$/, '')}/${buildAssetsDir.replace(/^\/+/, '')}`
    return assetsUrl.href
  }

  const refreshChunkHttpCache = async () => {
    const buildAssetsUrl = resolveBuildAssetsUrl()
    const bufferedUrls = performance.getEntriesByType('resource').map((entry) => entry.name)
    const chunkUrls = [...new Set([...bufferedUrls, ...observedScriptUrls])].filter(
      (url) => url.startsWith(buildAssetsUrl) && SCRIPT_URL_RE.test(url)
    )
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), HTTP_CACHE_REFRESH_TIMEOUT_MS)
    try {
      // 逐檔盡力而為：沒換到的檔案頂多在新頁面再失敗一次，並以 EXHAUSTED 上報
      await Promise.allSettled(
        chunkUrls.map(async (url) => {
          const response = await fetch(url, { cache: 'reload', signal: controller.signal })
          // body 讀完才會完整寫入 HTTP cache
          await response.arrayBuffer()
        })
      )
    } finally {
      clearTimeout(timeoutId)
    }
  }

  // A 類：清空所有快取（Cache Storage + HTTP cache 內的第一方 chunk）+ 反註冊 SW + 重整
  const purgeAndReload = async (reason) => {
    if (sessionStorage.getItem(PURGE_GUARD_KEY)) return
    sessionStorage.setItem(PURGE_GUARD_KEY, '1')
    isPurgeReloadPending = true
    console.error('[chunk-recovery] purge caches + reload:', reason)
    try {
      const { clearAllCaches, forceReload } = usePwaUpdate()
      // 先清 Cache Storage：同源部署時 SW 的 precache 會直接以快取回應，重抓就碰不到網路
      await clearAllCaches()
      // 重抓屬盡力而為，失敗不可連帶跳過 forceReload 的 SW 反註冊
      await refreshChunkHttpCache().catch((error) => console.error('[chunk-recovery] refresh chunk HTTP cache failed:', error))
      await forceReload()
    } catch (error) {
      console.error('[chunk-recovery] forceReload failed, hard reloading:', error)
      window.location.reload()
    }
  }

  // B 類：只重整，保留快取與 SW（precache 健在時重整幾乎瞬間完成）
  const reloadOnly = (reason) => {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
    console.warn('[chunk-recovery] reload without purging caches:', reason)
    window.location.reload()
  }

  const recover = (error, source) => {
    if (!(error instanceof Error)) return
    const message = error.message || ''
    if (CORRUPT_CHUNK_RE.test(message)) {
      // purgeAndReload 在第一個 await 之前就已同步決定是否接手復原，此處讀得到本次的結果
      purgeAndReload(`${source}: ${message}`)
      markChunkRecoveryStatus(error, isPurgeReloadPending ? CHUNK_RECOVERY_STATUS.RELOADING : CHUNK_RECOVERY_STATUS.EXHAUSTED)
      return
    }
    if (FETCH_CHUNK_RE.test(message)) {
      reloadOnly(`${source}: ${message}`)
    }
  }

  // Vue 非同步元件載入器（runtime-13）拋出的錯誤 → BD 與 1A9 的多數事件走這條
  nuxtApp.hook('vue:error', (error) => {
    recover(error, 'vue:error')
  })

  // Vite preload 失敗（多為 fetch 失敗），縱深補強
  nuxtApp.hook('app:chunkError', ({ error }) => {
    recover(error, 'app:chunkError')
  })

  // Vue render 邊界外的動態 import()（事件處理器 / composable 內）失敗時，其 rejection
  // 不會經過上述兩個 hook，改由全域 unhandledrejection 接住。
  // 不呼叫 event.preventDefault()，讓 rejection 仍正常上報以利 triage（guard 達上限後不再重載）。
  const handleUnhandledRejection = (event) => {
    recover(event?.reason, 'unhandledrejection')
  }
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  nuxtApp.hook('app:unmount', () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    scriptResourceObserver?.disconnect()
    chunkErrorNuxtApp = null
  })
})
